import { ChevronDown, ChevronRight, Search, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useGapReport, type Gap, type GapJD } from "@/api/jd-gap-queries";
import { useCan } from "@/auth/member";
import { DraftQuestionsDrawer, type GapSkill } from "@/components/jd-generate-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, Select } from "@/components/ui/input";
import { Empty, ErrorNote, Panel } from "@/components/ui/panel";
import { Segmented } from "@/components/ui/segmented";
import { Stat } from "@/components/ui/stat";
import { cn } from "@/lib/utils";

/**
 * The gap report — DESIGN §20.11, Phase 3. Every skill the JDs in use ask for that the bank cannot yet
 * ask about, one row per way of writing it, most-asked Must first: the same gap in five JDs is a gap in
 * the bank, and the one worth filling first. The fix differs by kind — a skill mapped to nothing is
 * mapped on its JD, one mapped to a stack with no questions needs questions — so the report filters by
 * it, and each JD under a row opens its Skills step or drafts questions in the JD page's own drawer.
 */

type Problem = "all" | "gap" | "uncovered";

const GRID =
  "grid grid-cols-[24px_minmax(160px,1.2fr)_100px_minmax(200px,1fr)_220px_100px] items-center gap-3 min-w-[980px]";

const STATE_WORDS: Record<GapJD["state"], string> = {
  gap: "Mapped to nothing",
  uncovered: "Mapped, but the bank has no questions there",
};

function GapRow({
  gap,
  widest,
  problem,
  canManage,
  onDraft,
}: {
  gap: Gap;
  widest: number;
  problem: Problem;
  canManage: boolean;
  onDraft: (jd: GapJD) => void;
}) {
  const [open, setOpen] = useState(false);
  const unmapped = gap.jds.filter((j) => j.state === "gap").length;
  const empty = gap.jds.length - unmapped;
  const shown = problem === "all" ? gap.jds : gap.jds.filter((j) => j.state === problem);
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cn(
          GRID,
          "border-bank-border hover:bg-bank-surface-2 w-full border-t px-4 py-2.5 text-left",
          open && "bg-bank-accent-wash",
        )}
      >
        {open ? (
          <ChevronDown className="size-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4" aria-hidden="true" />
        )}
        <span className="text-[13.5px] font-semibold">{gap.name}</span>
        <span className="text-bank-muted font-mono text-xs">{gap.category}</span>
        <span className="flex items-center gap-2.5">
          <span className="bg-bank-surface-3 h-2 flex-1 overflow-hidden rounded-full">
            <span
              className="bg-bank-accent block h-full rounded-full"
              style={{ width: `${(gap.jds.length / widest) * 100}%` }}
            />
          </span>
          <span className="text-[12.5px] whitespace-nowrap">
            {gap.jds.length} {gap.jds.length === 1 ? "JD" : "JDs"}
            {gap.must > 0 && <span className="text-bank-attention-ink"> · Must in {gap.must}</span>}
          </span>
        </span>
        <span className="text-[12.5px]">
          {unmapped > 0 && <span className="text-bank-attention-ink">Not mapped in {unmapped}</span>}
          {unmapped > 0 && empty > 0 && " · "}
          {empty > 0 && (
            <span>
              {unmapped ? "no" : "No"} questions in {empty}
            </span>
          )}
        </span>
        <span
          className={cn(
            "text-right text-[12.5px]",
            gap.filled > 0 ? "text-bank-good-ink" : "text-bank-muted",
          )}
        >
          {gap.filled} of {gap.jds.length}
        </span>
      </button>
      {open && (
        <ul className="bg-bank-surface-2">
          {shown.map((jd) => (
            <li
              key={`${jd.jd_id}-${jd.skill_id}`}
              className="border-bank-surface-3 grid min-w-[980px] grid-cols-[minmax(200px,1.3fr)_110px_60px_minmax(220px,1fr)_auto] items-center gap-3 border-t py-2 pr-4 pl-12"
            >
              <Link
                to={`/jds/${jd.jd_id}?step=skills`}
                className="text-bank-accent-ink text-[13px] font-medium hover:underline"
              >
                {jd.jd_title}
              </Link>
              <span>
                <Badge value={jd.jd_status} />
              </span>
              <span className="font-mono text-[11.5px]">{jd.importance}</span>
              <span className="text-[12.5px]">
                {jd.own_questions > 0 ? (
                  <span className="text-bank-good-ink">Filled — {jd.own_questions} of its own</span>
                ) : (
                  <span className={jd.state === "gap" ? "text-bank-attention-ink" : "text-bank-muted"}>
                    {STATE_WORDS[jd.state]}
                    {jd.stack_path ? ` (${jd.stack_path})` : jd.entity ? ` (keyword ${jd.entity})` : ""}
                  </span>
                )}
              </span>
              <span className="flex justify-end gap-1.5">
                <Link
                  to={`/jds/${jd.jd_id}?step=skills`}
                  className="border-bank-border-strong bg-bank-surface hover:bg-bank-surface-2 inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-medium"
                >
                  Open in Skills
                </Link>
                {canManage && (
                  <Button size="sm" onClick={() => onDraft(jd)}>
                    <Sparkles className="size-3.5" aria-hidden="true" />
                    Draft questions
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function JDGaps() {
  const report = useGapReport();
  const canManage = useCan("create:jd_gap_draft") || useCan("manage_jd");
  const [search, setSearch] = useState("");
  const [problem, setProblem] = useState<Problem>("all");
  const [category, setCategory] = useState("");
  const [draftFor, setDraftFor] = useState<GapSkill | null>(null);
  const data = report.data;
  const all = data?.items ?? [];
  const query = search.trim().toLowerCase();
  const items = all.filter(
    (g) =>
      (!category || g.category === category) &&
      (problem === "all" || g.jds.some((j) => j.state === problem)) &&
      (!query ||
        g.name.toLowerCase().includes(query) ||
        g.jds.some((j) => j.jd_title.toLowerCase().includes(query))),
  );
  const widest = Math.max(1, ...all.map((g) => g.jds.length));
  const withState = (state: GapJD["state"]) => all.filter((g) => g.jds.some((j) => j.state === state)).length;
  const affected = new Set(all.flatMap((g) => g.jds.map((j) => j.jd_id))).size;

  function draft(gap: Gap, jd: GapJD) {
    setDraftFor({
      jdId: jd.jd_id,
      skillId: jd.skill_id,
      name: gap.name,
      context: `${jd.importance} in “${jd.jd_title}” · ${STATE_WORDS[jd.state].toLowerCase()}`,
      stack: jd.stack_path,
      stacks: gap.category === "technical" ? (data?.technical_stacks ?? []) : (data?.domain_stacks ?? []),
    });
  }

  return (
    <div className="flex flex-col gap-3.5 p-4">
      <header>
        <h1 className="text-base font-semibold tracking-tight">Gap report</h1>
        <p className="text-bank-muted text-[12.5px]">
          Skills the job descriptions in review or live ask for that the bank can't ask about yet
          {data ? ` · ${data.jds_in_use} JD${data.jds_in_use === 1 ? "" : "s"} in use` : ""}. The same gap in
          five JDs is a gap in the bank — fill the most-asked first.
        </p>
      </header>

      {data && (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <Stat label="Gaps" value={all.length} hint="ways of writing a skill" />
          <Stat
            label="Asked as Must"
            value={all.filter((g) => g.must > 0).length}
            hint="fill these first"
            flag={all.some((g) => g.must > 0)}
          />
          <Stat label="JDs affected" value={affected} hint="in review or live" />
          <Stat
            label="Filled by a JD"
            value={all.filter((g) => g.filled > 0).length}
            hint="with questions of its own"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        <Segmented<Problem>
          label="Filter by problem"
          value={problem}
          onChange={setProblem}
          options={[
            { value: "all", label: "All", count: all.length },
            { value: "gap", label: "Not mapped", count: withState("gap") },
            { value: "uncovered", label: "Mapped, no questions", count: withState("uncovered") },
          ]}
        />
        <Select
          aria-label="Filter by type"
          className="w-44"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Technical &amp; domain</option>
          <option value="technical">Technical</option>
          <option value="domain">Domain</option>
        </Select>
        <label className="relative ml-auto block w-64 max-w-full">
          <Search className="text-bank-muted absolute top-2 left-2.5 size-4" aria-hidden="true" />
          <Input
            type="search"
            className="pl-8"
            aria-label="Search skills or JDs"
            placeholder="Search skills or JDs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      <ErrorNote error={report.error} />
      <Panel className="overflow-hidden">
        {report.isLoading ? (
          <p className="text-bank-muted p-4 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <Empty>
            {all.length && (query || category || problem !== "all")
              ? "Nothing matches that."
              : "No gaps: every skill the JDs in use ask for has questions behind it."}
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <div
              className={cn(
                GRID,
                "bg-bank-surface-2 text-bank-muted px-4 py-2 font-mono text-[10.5px] tracking-[0.07em] uppercase",
              )}
            >
              <span />
              <span>Skill</span>
              <span>Type</span>
              <span>Asked by</span>
              <span>Problem</span>
              <span className="text-right">Filled</span>
            </div>
            <ul>
              {items.map((gap) => (
                <GapRow
                  key={`${gap.key}-${gap.category}`}
                  gap={gap}
                  widest={widest}
                  problem={problem}
                  canManage={canManage}
                  onDraft={(jd) => draft(gap, jd)}
                />
              ))}
            </ul>
          </div>
        )}
      </Panel>
      <p className="text-bank-muted text-xs">
        <strong className="text-bank-ink">Not mapped</strong>: the skill points at no stack — choose one on
        the JD's Skills step. <strong className="text-bank-ink">Mapped, no questions</strong>: it points at a
        stack or keyword with no approved questions — draft some, or add them through Ingest. Drafted
        questions wait on the JD's Questions step.
      </p>

      <DraftQuestionsDrawer
        gap={draftFor}
        maxPerRun={data?.max_per_run ?? 50}
        onClose={() => setDraftFor(null)}
      />
    </div>
  );
}
